import { Color } from './java/Color';
import { Graphics2D, Font, FontMetrics, BasicStroke, RenderingHints } from './java/Graphics2D';
import { Rectangle } from './java/Geom';
import { Random } from './java/Random';
import { GameObject } from './GameObject';
import { Handler } from './Handler';
import { ID } from './ID';
import { Game } from './Game';
import { EnemyBossBullet } from './EnemyBossBullet';
import { PageRenderer } from './PageRenderer';

/**
 * Laser Boss — hovers in the center of the arena, rotating laser beam patterns.
 * Fires sweeping beams that the player must dodge by reading the telegraph and moving.
 * The beams are rendered as thick lines with glow. Multiple patterns rotate through phases.
 */
export class BossLaser extends GameObject {

  private static readonly SIZE = 80;
  private static readonly R = 40; // circular boss

  private handler: Handler;
  private r: Random = new Random();
  private pulsePhase = 0;
  private readonly boundsRect: Rectangle = new Rectangle();
  private static readonly EMPTY_BOUNDS: Rectangle = new Rectangle(0, 0, 0, 0);

  // Entry
  private entryTimer = 100;
  private warmupTimer = 40;

  // Health
  private maxHp = 1400;
  private hp: number;
  private defeated = false;
  private deathTimer = 0;

  // Laser system
  private beamCount = 2;        // active beams
  private beamAngle = 0;        // current rotation angle
  private beamSpeed = 0.012;    // radians per tick
  private patternTimer = 0;
  private pattern = 0;          // 0=sweep, 1=cross, 2=spiral
  private telegraphAlpha = 0;   // flashes before beam activates
  private beamsActive = false;
  private telegraphTimer = 0;
  private static readonly TELEGRAPH_DURATION = 60;
  private static readonly PATTERN_DURATION = 300;

  // Beam damage — dealt through collision rectangles
  private static readonly BEAM_LENGTH = 900;
  private static readonly BEAM_WIDTH = 12;

  // Phase thresholds
  private static readonly PHASE2 = 0.55;
  private static readonly PHASE3 = 0.2;

  // Colors — electric blue theme
  private static readonly FILL: Color = new Color(80, 140, 255);
  private static readonly FILL_P2: Color = new Color(120, 100, 255);
  private static readonly FILL_P3: Color = new Color(180, 80, 255);
  private static readonly BEAM_COLOR: Color = new Color(80, 160, 255);
  private static readonly BEAM_GLOW: Color = new Color(80, 160, 255, 30);
  private static readonly TELEGRAPH_COLOR: Color = new Color(80, 160, 255, 40);
  private static readonly BAR_BG: Color = new Color(22, 30, 44);
  private static readonly BAR_BORDER: Color = new Color(40, 52, 70);
  private static readonly FONT_BOSS: Font = new Font('Arial', Font.BOLD, 14);
  private static readonly FONT_PHASE: Font = new Font('Arial', Font.BOLD, 11);

  constructor(x: number, y: number, id: ID, handler: Handler) {
    super(x, y, id);
    this.handler = handler;
    this.hp = this.maxHp;
    this.velX = 0;
    this.velY = 2;
  }

  setMaxHp(hp: number): void {
    this.maxHp = hp;
    this.hp = hp;
  }

  isDefeated(): boolean {
    return this.defeated && this.deathTimer > 60;
  }

  takeDamage(dmg: number): boolean {
    if (this.defeated) return false;
    this.hp -= dmg;
    if (this.hp < 0) this.hp = 0;
    return true;
  }

  getHpPercent(): number { return this.hp / this.maxHp; }

  getPhase(): number {
    const pct = this.hp / this.maxHp;
    if (pct <= BossLaser.PHASE3) return 3;
    if (pct <= BossLaser.PHASE2) return 2;
    return 1;
  }

  getBounds(): Rectangle {
    if (this.defeated || this.entryTimer > 0) return BossLaser.EMPTY_BOUNDS;
    this.boundsRect.x = Math.trunc(this.x);
    this.boundsRect.y = Math.trunc(this.y);
    this.boundsRect.width = BossLaser.SIZE;
    this.boundsRect.height = BossLaser.SIZE;
    return this.boundsRect;
  }

  tick(): void {
    if (this.defeated) {
      this.deathTimer++;
      return;
    }

    this.pulsePhase += 0.06;

    // Entry — slide to center
    if (this.entryTimer > 0) {
      this.x += this.velX;
      this.y += this.velY;
      this.entryTimer--;
      if (this.entryTimer <= 0) {
        this.velX = 0;
        this.velY = 0;
        // Snap to center
        this.x = Math.trunc(Game.WIDTH / 2) - Math.trunc(BossLaser.SIZE / 2);
        this.y = Math.trunc(Game.HEIGHT / 2) - Math.trunc(BossLaser.SIZE / 2);
      }
      return;
    }
    if (this.warmupTimer > 0) {
      this.warmupTimer--;
      if (this.warmupTimer <= 0) {
        this.startNewPattern();
      }
      return;
    }

    // HP drains over time
    let drainRate = 0.7 + (1 - this.hp / this.maxHp) * 0.35;
    if (Game.combatMode) drainRate *= 0.35;
    this.hp -= drainRate;

    if (this.hp <= 0) {
      this.hp = 0;
      this.defeated = true;
      Game.triggerBulletCascade(
        this.x + Math.trunc(BossLaser.SIZE / 2),
        this.y + Math.trunc(BossLaser.SIZE / 2),
        this.handler,
      );
      return;
    }

    // Slight hovering movement
    const hoverX = Math.sin(this.pulsePhase * 0.4) * 0.3;
    const hoverY = Math.cos(this.pulsePhase * 0.3) * 0.2;
    this.x += hoverX;
    this.y += hoverY;

    // Telegraph phase — warn player before beams activate
    if (this.telegraphTimer > 0) {
      this.telegraphTimer--;
      this.telegraphAlpha = Math.sin(this.telegraphTimer * 0.15) * 0.5 + 0.5;
      if (this.telegraphTimer <= 0) {
        this.beamsActive = true;
      }
      return;
    }

    // Pattern execution
    if (this.beamsActive) {
      const phase = this.getPhase();
      const speedMult = phase === 3 ? 1.6 : phase === 2 ? 1.3 : 1;

      switch (this.pattern) {
        case 0: // Steady sweep
          this.beamAngle += this.beamSpeed * speedMult;
          break;
        case 1: // Cross — 4 beams, slower rotation
          this.beamAngle += this.beamSpeed * 0.7 * speedMult;
          break;
        case 2: { // Spiral — accelerating rotation
          const accel = 1 + this.patternTimer * 0.002;
          this.beamAngle += this.beamSpeed * accel * speedMult;
          break;
        }
      }

      // Check beam collision with player
      this.checkBeamCollision();

      this.patternTimer++;
      if (this.patternTimer >= BossLaser.PATTERN_DURATION) {
        this.beamsActive = false;
        this.startNewPattern();
      }

      // Occasional bullet spray between beam sweeps (phase 2+)
      if (phase >= 2 && this.patternTimer % (phase === 3 ? 40 : 60) === 0) {
        const count = phase === 3 ? 6 : 4;
        for (let i = 0; i < count; i++) {
          const a = (Math.PI * 2 * i / count) + this.pulsePhase;
          this.handler.addObject(new EnemyBossBullet(
            Math.trunc(this.x + Math.trunc(BossLaser.SIZE / 2)),
            Math.trunc(this.y + Math.trunc(BossLaser.SIZE / 2)),
            ID.BasicEnemy, this.handler,
            Math.cos(a) * 3, Math.sin(a) * 3));
        }
      }
    }
  }

  private startNewPattern(): void {
    this.pattern = (this.pattern + 1) % 3;
    this.patternTimer = 0;

    const phase = this.getPhase();
    switch (this.pattern) {
      case 0: this.beamCount = phase >= 2 ? 3 : 2; break;
      case 1: this.beamCount = 4; break;
      case 2: this.beamCount = phase === 3 ? 3 : 2; break;
    }
    this.beamSpeed = 0.012 + phase * 0.003;
    this.telegraphTimer = BossLaser.TELEGRAPH_DURATION;
    this.beamsActive = false;
  }

  private checkBeamCollision(): void {
    const player = this.findPlayer();
    if (player === null) return;

    const cx = this.x + Math.trunc(BossLaser.SIZE / 2);
    const cy = this.y + Math.trunc(BossLaser.SIZE / 2);
    const px = player.getX() + 24;
    const py = player.getY() + 24;

    for (let i = 0; i < this.beamCount; i++) {
      const angle = this.beamAngle + i * (Math.PI * 2 / this.beamCount);

      // Point-to-line distance check
      const dx = Math.cos(angle);
      const dy = Math.sin(angle);

      // Project player position onto beam line
      const relX = px - cx;
      const relY = py - cy;
      const dot = relX * dx + relY * dy;

      if (dot > 30 && dot < BossLaser.BEAM_LENGTH) { // beyond the boss body, within beam range
        const perpDist = Math.abs(relX * dy - relY * dx);
        if (perpDist < BossLaser.BEAM_WIDTH + 12) { // 12 = player half-size
          // Beam hit — damage is handled through boss body collision in Player
          // Create a temporary bullet at player position to trigger hit
          this.handler.addObject(new EnemyBossBullet(
            Math.trunc(px) - 4, Math.trunc(py) - 4,
            ID.BasicEnemy, this.handler, 0, 0));
        }
      }
    }
  }

  private findPlayer(): GameObject | null {
    for (let i = 0; i < this.handler.getObjects().length; i++) {
      if (this.handler.getObjects()[i].getId() === ID.Player)
        return this.handler.getObjects()[i];
    }
    return null;
  }

  render(g: Graphics2D): void {
    const g2 = g;
    g2.setRenderingHint(RenderingHints.KEY_ANTIALIASING, RenderingHints.VALUE_ANTIALIAS_ON);
    const ix = Math.trunc(this.x), iy = Math.trunc(this.y);

    if (this.defeated) {
      const t = Math.min(this.deathTimer / 60, 1);
      const shrink = Math.trunc(t * BossLaser.SIZE / 2);
      const alpha = Math.trunc((1 - t) * 255);
      if (alpha > 0) {
        g2.setColor(new Color(255, 255, 255, alpha));
        g2.fillOval(ix + shrink, iy + shrink, BossLaser.SIZE - shrink * 2, BossLaser.SIZE - shrink * 2);
      }
      return;
    }

    const fill = this.getPhase() === 3 ? BossLaser.FILL_P3 : this.getPhase() === 2 ? BossLaser.FILL_P2 : BossLaser.FILL;
    const pulse = Math.sin(this.pulsePhase) * 0.5 + 0.5;
    const cx = ix + Math.trunc(BossLaser.SIZE / 2), cy = iy + Math.trunc(BossLaser.SIZE / 2);

    // Draw beams (behind boss)
    if (this.beamsActive || this.telegraphTimer > 0) {
      this.renderBeams(g2, cx, cy, fill);
    }

    // Glow
    const glowSize = 8 + Math.trunc(4 * pulse);
    g2.setColor(new Color(fill.getRed(), fill.getGreen(), fill.getBlue(), 12 + Math.trunc(10 * pulse)));
    g2.fillOval(cx - Math.trunc(BossLaser.SIZE / 2) - glowSize, cy - Math.trunc(BossLaser.SIZE / 2) - glowSize,
      BossLaser.SIZE + glowSize * 2, BossLaser.SIZE + glowSize * 2);

    // Main body — circle with inner rings
    g2.setColor(fill);
    g2.fillOval(ix, iy, BossLaser.SIZE, BossLaser.SIZE);
    g2.setColor(new Color(fill.getRed(), fill.getGreen(), fill.getBlue(), 80 + Math.trunc(60 * pulse)));
    g2.drawOval(ix, iy, BossLaser.SIZE, BossLaser.SIZE);

    // Inner concentric rings
    g2.setColor(new Color(255, 255, 255, 20 + Math.trunc(15 * pulse)));
    g2.drawOval(cx - Math.trunc(BossLaser.SIZE / 4), cy - Math.trunc(BossLaser.SIZE / 4),
      Math.trunc(BossLaser.SIZE / 2), Math.trunc(BossLaser.SIZE / 2));
    g2.drawOval(cx - Math.trunc(BossLaser.SIZE / 6), cy - Math.trunc(BossLaser.SIZE / 6),
      Math.trunc(BossLaser.SIZE / 3), Math.trunc(BossLaser.SIZE / 3));

    // Rotating beam origin indicators
    for (let i = 0; i < this.beamCount; i++) {
      const angle = this.beamAngle + i * (Math.PI * 2 / this.beamCount);
      const dotX = cx + Math.trunc(Math.cos(angle) * (Math.trunc(BossLaser.SIZE / 2) - 6));
      const dotY = cy + Math.trunc(Math.sin(angle) * (Math.trunc(BossLaser.SIZE / 2) - 6));
      g2.setColor(new Color(255, 255, 255, 120 + Math.trunc(80 * pulse)));
      g2.fillOval(dotX - 3, dotY - 3, 6, 6);
    }

    this.renderBossBar(g2);
  }

  private renderBeams(g: Graphics2D, cx: number, cy: number, fill: Color): void {
    const oldStroke = g.getStroke();

    for (let i = 0; i < this.beamCount; i++) {
      const angle = this.beamAngle + i * (Math.PI * 2 / this.beamCount);
      const endX = cx + Math.trunc(Math.cos(angle) * BossLaser.BEAM_LENGTH);
      const endY = cy + Math.trunc(Math.sin(angle) * BossLaser.BEAM_LENGTH);

      if (this.beamsActive) {
        // Wide glow
        g.setStroke(new BasicStroke(BossLaser.BEAM_WIDTH * 3, BasicStroke.CAP_ROUND, BasicStroke.JOIN_ROUND));
        g.setColor(BossLaser.BEAM_GLOW);
        g.drawLine(cx, cy, endX, endY);

        // Core beam
        g.setStroke(new BasicStroke(BossLaser.BEAM_WIDTH, BasicStroke.CAP_ROUND, BasicStroke.JOIN_ROUND));
        g.setColor(new Color(fill.getRed(), fill.getGreen(), fill.getBlue(), 180));
        g.drawLine(cx, cy, endX, endY);

        // Bright center
        g.setStroke(new BasicStroke(BossLaser.BEAM_WIDTH / 3, BasicStroke.CAP_ROUND, BasicStroke.JOIN_ROUND));
        g.setColor(new Color(255, 255, 255, 160));
        g.drawLine(cx, cy, endX, endY);
      } else {
        // Telegraph — dashed thin line
        const alpha = Math.trunc(this.telegraphAlpha * 60);
        g.setStroke(new BasicStroke(2, BasicStroke.CAP_BUTT, BasicStroke.JOIN_MITER,
          10, [10, 10], this.pulsePhase * 20));
        g.setColor(new Color(fill.getRed(), fill.getGreen(), fill.getBlue(), alpha));
        g.drawLine(cx, cy, endX, endY);
      }
    }

    g.setStroke(oldStroke);
  }

  private renderBossBar(g: Graphics2D): void {
    const barW = 400;
    const barH = 10;
    const barX = Math.trunc((Game.WIDTH - barW) / 2);
    const barY = Game.HEIGHT - 40;
    const pct = this.hp / this.maxHp;

    g.setColor(BossLaser.BAR_BG);
    g.fillRoundRect(barX, barY, barW, barH, 5, 5);

    const fill = this.getPhase() === 3 ? BossLaser.FILL_P3 : this.getPhase() === 2 ? BossLaser.FILL_P2 : BossLaser.FILL;
    const fillW = Math.trunc(barW * pct);
    if (fillW > 0) {
      g.setColor(fill);
      g.fillRoundRect(barX, barY, fillW, barH, 5, 5);
    }

    g.setColor(BossLaser.BAR_BORDER);
    g.drawRoundRect(barX, barY, barW, barH, 5, 5);

    g.setFont(BossLaser.FONT_BOSS);
    g.setColor(PageRenderer.TEXT_SEC);
    const fm: FontMetrics = g.getFontMetrics();
    g.drawString('LASER', barX - fm.stringWidth('LASER') - 10, barY + 9);

    g.setFont(BossLaser.FONT_PHASE);
    g.setColor(fill);
    g.drawString('Phase ' + this.getPhase(), barX + barW + 10, barY + 9);
  }
}
